import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PageShell from "@/components/PageShell";
import ShareButtons from "@/components/ShareButtons";
import { formatDate, sortedPosts } from "@/pages/Blog";

const PROSE =
    "mt-10 max-w-3xl text-lg leading-relaxed text-foreground/85 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-primary sm:[&_h2]:text-3xl " +
    "[&_h3]:mt-8 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-primary [&_h4]:mt-6 [&_h4]:font-bold [&_p]:mt-4 " +
    "[&_ul]:mt-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_ol]:mt-4 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 " +
    "[&_a]:font-semibold [&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline [&_strong]:text-foreground " +
    "[&_blockquote]:mt-6 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-5 [&_blockquote]:italic " +
    "[&_table]:mt-6 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-border [&_th]:bg-surface-2 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left";

export default function BlogPost() {
    const { slug } = useParams();
    const post = sortedPosts.find((p) => p.slug === slug);
    if (!post) return <Navigate to="/blog" replace />;

    const related = sortedPosts.filter((p) => p.slug !== slug).slice(0, 3);

    return (
        <PageShell testId="blog-post-page">
            <article className="mx-auto max-w-7xl px-5 pb-20 pt-14 md:px-8 md:pt-20">
                <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="blog-back-link">
                    <ArrowLeft size={14} /> Back to the blog
                </Link>
                <time dateTime={post.date} className="mt-6 block text-sm text-muted-foreground" data-testid="blog-post-date">{formatDate(post.date)}</time>
                <h1 className="mt-2 max-w-4xl font-display text-4xl font-bold leading-tight text-primary sm:text-5xl" data-testid="blog-post-title">{post.title}</h1>
                <div className="mt-6"><ShareButtons title={post.title} /></div>
                {post.disclaimer && (
                    <p className="mt-6 max-w-3xl rounded-lg border border-border bg-surface-2 px-5 py-3 text-base text-foreground/80" data-testid="blog-post-disclaimer">
                        This article was published on the date shown and reflects the rules and rates that applied at the time. Tax, superannuation and payroll rules change — check current ATO guidance or speak to us before acting on it.
                    </p>
                )}
                {/* Article HTML is PayMe's own editorial content, sanitised at import time to a fixed tag whitelist. */}
                <div className={PROSE} data-testid="blog-post-body" dangerouslySetInnerHTML={{ __html: post.html }} />
                <div className="mt-10 border-t border-border pt-6"><ShareButtons title={post.title} suffix="-bottom" /></div>

                <aside className="mt-16 border-t border-border pt-10">
                    <h2 className="font-display text-2xl font-bold text-primary">Related Contractor News</h2>
                    <ul className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="blog-related">
                        {related.map((p) => (
                            <li key={p.slug}>
                                <Link to={`/blog/${p.slug}`} className="block rounded-lg border border-border bg-white p-5 transition-shadow hover:shadow-md">
                                    <time className="text-xs text-muted-foreground">{formatDate(p.date)}</time>
                                    <p className="mt-1 font-display text-lg font-bold leading-snug text-primary">{p.title}</p>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </aside>
            </article>
        </PageShell>
    );
}
